import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Star, ChevronDown, ChevronUp, Navigation, Instagram, ExternalLink } from 'lucide-react';
import BookingModal from '../components/BookingModal';

// FAQ Data
const faqs = [
  {
    question: "How much do nail extensions cost in Basaveshwarnagar?",
    answer: "At Nailashes Basaveshwarnagar, nail extension prices start from ₹1,499 for gel extensions and ₹1,999 for acrylic extensions. The final price depends on the length, shape, and design complexity you choose. We offer transparent pricing with no hidden charges. For custom nail art or premium designs, prices may vary. Book a free consultation to get an exact quote."
  },
  {
    question: "How long do gel nail extensions last?",
    answer: "Gel nail extensions typically last 3-4 weeks with proper care. We recommend getting infills every 2-3 weeks to maintain the look and health of your natural nails. Our technicians use premium quality products that ensure longer-lasting results. Factors like your daily activities and nail care routine can affect longevity."
  },
  {
    question: "Do you provide nail extension removal services?",
    answer: "Yes, we offer professional nail extension removal at ₹299. We never recommend removing extensions at home as improper removal can damage your natural nails. Our technicians use acetone-free removal methods where possible and always follow up with nail strengthening treatments to keep your natural nails healthy."
  },
  {
    question: "Do I need an appointment or can I walk in?",
    answer: "While we welcome walk-ins, we highly recommend booking an appointment to avoid waiting. Our Basaveshwarnagar salon gets busy, especially on weekends. You can book via phone at +91 9611560759, WhatsApp, or through our website. Appointments ensure you get your preferred time slot and technician."
  },
  {
    question: "Do you offer lash extensions at Nailashes Basaveshwarnagar?",
    answer: "Yes! We offer a full range of lash extension services including Classic Lashes (₹2,499), Volume Lashes (₹3,499), and Hybrid Lashes (₹2,999). Our lash technicians are certified and use premium silk and mink lashes. Lash extensions typically last 4-6 weeks with proper care."
  },
  {
    question: "Where exactly is Nailashes Basaveshwarnagar located?",
    answer: "We're located at LGF, No. 9, East Jayanagar, West of Chord Road 1st Stage, Basaveshwar Nagar, Bengaluru 560079. We're near the West of Chord Road junction, easily accessible from Rajajinagar, Vijayanagar, and Mahalakshmi Layout. Look for the Nailashes signboard on the lower ground floor."
  },
  {
    question: "What are the salon timings?",
    answer: "Nailashes Basaveshwarnagar is open every day from 10:30 AM to 10:00 PM, including weekends and most holidays. We recommend arriving 10 minutes before your appointment time."
  },
  {
    question: "Do you offer bridal nail packages?",
    answer: "Absolutely! We have special bridal packages starting from ₹4,999 that include consultation, trial session, and the final bridal nail art. We also offer bride tribe packages with group discounts for bridesmaids. Book at least 2 weeks in advance for bridal services."
  }
];

// Service sections data
const servicesSections = [
  {
    id: 'nail-extensions',
    title: 'Nail Extensions in Basaveshwarnagar',
    description: `Looking for the best nail extensions in Basaveshwarnagar? Nailashes offers premium nail extension services that transform your nails into stunning works of art. Our certified technicians specialize in creating natural-looking extensions that are durable, comfortable, and absolutely gorgeous.

We offer multiple extension techniques including tip extensions, sculpted extensions, and overlay extensions. Each method is suited to different nail types and preferences. During your consultation, our experts will assess your natural nails and recommend the best approach for achieving your desired look.

Our nail extensions are perfect for special occasions like weddings, engagements, and parties, or simply for everyday glamour. We use only salon-grade products that are gentle on your natural nails while providing a flawless, long-lasting finish.`,
    services: [
      { name: 'Gel Nail Extensions', price: '₹1,499', duration: '90 mins', description: 'Lightweight, natural-looking extensions using premium gel' },
      { name: 'Acrylic Extensions', price: '₹1,999', duration: '120 mins', description: 'Strong, durable extensions ideal for long nails' },
      { name: 'Polygel Extensions', price: '₹1,799', duration: '100 mins', description: 'Combines benefits of gel and acrylic - lightweight yet strong' },
      { name: 'Fiberglass Extensions', price: '₹1,599', duration: '90 mins', description: 'Thin, natural-looking extensions for subtle enhancement' },
    ]
  },
  {
    id: 'gel-nails',
    title: 'Gel Nails & Gel Polish',
    description: `Gel nails are the most popular choice among our Basaveshwarnagar clients. The high-shine finish, chip-resistant formula, and 2-3 week longevity make gel polish the perfect choice for busy professionals and anyone who wants hassle-free beautiful nails.

At Nailashes, we use premium gel polish brands including OPI GelColor, Gelish, and CND Shellac. These professional-grade products provide vibrant colors, excellent wear, and safe removal. Our extensive color range includes over 200 shades - from classic nudes and reds to trendy neons and glitters.

Our gel manicure process includes thorough nail prep, cuticle care, gel application, and LED curing. The result? Instantly dry, perfectly glossy nails that last for weeks without chipping or fading.`,
    services: [
      { name: 'Gel Polish Manicure', price: '₹799', duration: '45 mins', description: 'Long-lasting gel polish on natural nails' },
      { name: 'Builder Gel Overlay', price: '₹999', duration: '60 mins', description: 'Strengthening gel layer over natural nails' },
      { name: 'Gel Polish Pedicure', price: '₹999', duration: '60 mins', description: 'Gel polish application for toenails' },
      { name: 'Gel Removal + Manicure', price: '₹599', duration: '45 mins', description: 'Safe removal with nail care treatment' },
    ]
  },
  {
    id: 'nail-art',
    title: 'Nail Art & Custom Designs',
    description: `Express your personality through stunning nail art at Nailashes Basaveshwarnagar. Our nail artists are trained in the latest techniques and trends from Instagram, Pinterest, and international nail competitions. From minimalist designs to elaborate 3D art, we can create any look you desire.

Popular nail art styles at our salon include French tips, ombre/gradient nails, marble effect, geometric patterns, floral designs, and character art. We also specialize in festival-themed nail art for Diwali, Christmas, and other celebrations.

For truly unique designs, bring us your inspiration photos and we'll recreate or customize them to suit your style. Our artists love creative challenges and take pride in delivering Instagram-worthy nails that get you compliments everywhere you go.`,
    services: [
      { name: 'Basic Nail Art', price: '₹100/nail', duration: '15 mins', description: 'Simple designs, dots, lines, and French tips' },
      { name: '3D Nail Art', price: '₹200/nail', duration: '25 mins', description: 'Sculpted flowers, bows, and dimensional designs' },
      { name: 'Chrome/Mirror Nails', price: '₹1,299', duration: '75 mins', description: 'Stunning metallic mirror finish' },
      { name: 'Ombre/Gradient Nails', price: '₹1,199', duration: '70 mins', description: 'Beautiful color fade effect' },
      { name: 'Encapsulated Art', price: '₹250/nail', duration: '30 mins', description: 'Designs sealed within gel layers' },
    ]
  },
  {
    id: 'manicure-pedicure',
    title: 'Manicure & Pedicure Services',
    description: `Treat yourself to a relaxing manicure and pedicure at Nailashes Basaveshwarnagar. Our mani-pedi services go beyond basic nail care - they're a complete pampering experience for your hands and feet.

Every manicure includes nail shaping, cuticle care, hand massage, and polish application. Our pedicures feature relaxing foot soaks, exfoliation, callus treatment, and moisturizing massage. We use premium products that leave your skin soft, smooth, and rejuvenated.

For the ultimate indulgence, try our Spa or Luxury packages that include additional treatments like paraffin wax, hot stone massage, and aromatherapy. These services are perfect for unwinding after a long week or preparing for a special occasion.`,
    services: [
      { name: 'Classic Manicure', price: '₹499', duration: '45 mins', description: 'Essential nail care with regular polish' },
      { name: 'Spa Manicure', price: '₹799', duration: '60 mins', description: 'Includes scrub, mask, and extended massage' },
      { name: 'Classic Pedicure', price: '₹599', duration: '45 mins', description: 'Foot care with soak and polish' },
      { name: 'Spa Pedicure', price: '₹899', duration: '75 mins', description: 'Complete foot therapy with callus treatment' },
      { name: 'Luxury Mani-Pedi Combo', price: '₹1,999', duration: '120 mins', description: 'Premium package with paraffin and hot stones' },
    ]
  },
  {
    id: 'lash-extensions',
    title: 'Lash Extensions & Lifts',
    description: `Wake up with perfect lashes every day with our professional lash extension services. Nailashes Basaveshwarnagar offers a range of lash styles from natural-looking classic lashes to dramatic volume sets.

Our lash technicians are certified and experienced in all major lash techniques. We use premium silk and synthetic mink lashes that are lightweight, comfortable, and safe for your natural lashes. Each extension is individually applied to your natural lash for a seamless, undetectable finish.

Whether you want a subtle everyday enhancement or glamorous lashes for a special event, we customize the length, curl, and thickness to flatter your eye shape and match your lifestyle. Lash extensions eliminate the need for mascara and look stunning 24/7.`,
    services: [
      { name: 'Classic Lash Extensions', price: '₹2,499', duration: '90 mins', description: 'One extension per natural lash - natural look' },
      { name: 'Volume Lash Extensions', price: '₹3,499', duration: '120 mins', description: 'Multiple fine extensions - fuller, dramatic look' },
      { name: 'Hybrid Lashes', price: '₹2,999', duration: '105 mins', description: 'Mix of classic and volume - textured look' },
      { name: 'Lash Lift & Tint', price: '₹1,499', duration: '60 mins', description: 'Semi-permanent curl for natural lashes' },
      { name: 'Lash Infills', price: '₹999', duration: '45 mins', description: 'Maintenance fills every 2-3 weeks' },
    ]
  },
  {
    id: 'pmu',
    title: 'Permanent Makeup (PMU)',
    description: `Enhance your natural beauty with our permanent makeup services at Nailashes Basaveshwarnagar. PMU is perfect for those who want to wake up with flawless brows, defined eyes, or tinted lips without the daily makeup routine.

Our PMU artists use the latest microblading and machine techniques to create natural-looking results that last 1-3 years. We prioritize safety with single-use needles, premium pigments, and strict hygiene protocols.

Before any PMU procedure, we conduct a detailed consultation to understand your desired outcome and perform a patch test. We create custom designs that complement your face shape and features, ensuring results you'll love.`,
    services: [
      { name: 'Microblading Eyebrows', price: '₹8,999', duration: '150 mins', description: 'Hair-stroke technique for natural brows' },
      { name: 'Ombre/Powder Brows', price: '₹9,999', duration: '150 mins', description: 'Soft, filled-in brow look' },
      { name: 'Lip Blush', price: '₹12,999', duration: '180 mins', description: 'Natural tinted lip enhancement' },
      { name: 'Eyeliner PMU', price: '₹6,999', duration: '120 mins', description: 'Permanent eyeliner - various styles' },
      { name: 'PMU Touch-up', price: '₹3,999', duration: '90 mins', description: 'Refresh session within 6-8 weeks' },
    ]
  }
];

const NailashesBasaveshwarnagar = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [expandedSection, setExpandedSection] = useState('nail-extensions');

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "NailSalon",
    "name": "Nailashes Basaveshwarnagar",
    "alternateName": ["Nailashes", "Nailashes Bangalore", "BLR Nail Club Basaveshwarnagar"],
    "description": "Premium nail salon in Basaveshwarnagar, Bangalore offering nail extensions, gel nails, nail art, manicure, pedicure, lash extensions and permanent makeup services.",
    "url": "https://www.blrnailclub.in/nailashes-basaveshwarnagar",
    "telephone": "+919611560759",
    "email": "blrnailclub@gmail.com",
    "priceRange": "₹₹",
    "image": "https://www.blrnailclub.in/logo.png",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "LGF, No. 9, East Jayanagar, West of Chord Road 1st Stage",
      "addressLocality": "Basaveshwar Nagar",
      "addressRegion": "Karnataka",
      "postalCode": "560079",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 12.9916,
      "longitude": 77.5369
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "10:30",
        "closes": "22:00"
      }
    ],
    "sameAs": [
      "https://www.instagram.com/nailashes_basaveshwarnagar/",
      "https://www.facebook.com/nailashesbasaveshwarnagar"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Nail Services",
      "itemListElement": [
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Gel Nail Extensions"}, "price": "1499", "priceCurrency": "INR"},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Acrylic Extensions"}, "price": "1999", "priceCurrency": "INR"},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Classic Manicure"}, "price": "499", "priceCurrency": "INR"},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Spa Pedicure"}, "price": "899", "priceCurrency": "INR"},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Lash Extensions"}, "price": "2499", "priceCurrency": "INR"}
      ]
    },
    "areaServed": [
      {"@type": "Place", "name": "Basaveshwarnagar"},
      {"@type": "Place", "name": "Rajajinagar"},
      {"@type": "Place", "name": "Vijayanagar"},
      {"@type": "Place", "name": "Mahalakshmi Layout"},
      {"@type": "City", "name": "Bangalore"}
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Nailashes – Premium Nail Salon in Basaveshwarnagar, Bangalore | Nail Extensions, Gel Nails, Lash Extensions</title>
        <meta name="description" content="Nailashes Basaveshwarnagar - Bangalore's trusted nail salon. Expert nail extensions, gel nails, nail art, manicure, pedicure, lash extensions & PMU. Highly rated on Google. Open 10:30 AM - 10 PM. Book now!" />
        <meta name="keywords" content="Nailashes Basaveshwarnagar, nail salon Basaveshwarnagar, nail extensions Basaveshwarnagar, gel nails Basaveshwarnagar, nail art Basaveshwarnagar, manicure pedicure Basaveshwarnagar, lash extensions Basaveshwarnagar, PMU Basaveshwarnagar, best nail salon West Bangalore" />
        <link rel="canonical" href="https://www.blrnailclub.in/nailashes-basaveshwarnagar" />
        
        <meta property="og:type" content="business.business" />
        <meta property="og:title" content="Nailashes – Premium Nail Salon in Basaveshwarnagar, Bangalore" />
        <meta property="og:description" content="Expert nail extensions, gel nails, nail art, lash extensions & more. Highly rated on Google. Book your appointment today!" />
        <meta property="og:url" content="https://www.blrnailclub.in/nailashes-basaveshwarnagar" />
        <meta property="og:image" content="https://www.blrnailclub.in/logo.png" />
        
        <meta name="geo.region" content="IN-KA" />
        <meta name="geo.placename" content="Basaveshwarnagar, Bangalore" />
        <meta name="geo.position" content="12.9916;77.5369" />
        
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <main className="bg-black min-h-screen" data-testid="nailashes-basaveshwarnagar-page">
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-end pb-16 pt-28 md:pt-0">
          <div className="absolute inset-0">
            <img
              src="https://images.pexels.com/photos/3997391/pexels-photo-3997391.jpeg?auto=compress&cs=tinysrgb&w=1920"
              alt="Nailashes Basaveshwarnagar nail salon interior"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30"></div>
          </div>
          
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12">
            {/* Nailashes Brand Identity */}
            <div className="mb-6">
              <span className="inline-block bg-[#B8860B] text-black text-xs tracking-[0.2em] uppercase px-4 py-2 font-medium">
                India's Largest Nail Chain
              </span>
            </div>
            
            <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-light tracking-wide mb-4">
              Nailashes – Premium Nail Salon<br />
              <span className="text-[#B8860B]">in Basaveshwarnagar, Bangalore</span>
            </h1>
            
            <p className="text-white/70 text-lg md:text-xl max-w-2xl mb-8">
              Expert nail extensions, gel nails, nail art, lash extensions & permanent makeup. 
              Serving Basaveshwarnagar, Rajajinagar, Vijayanagar & West Bangalore.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="bg-[#B8860B] text-black px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#9A7B0A] transition-colors"
                data-testid="hero-book-now"
              >
                Book Appointment
              </button>
              <a
                href="tel:+919611560759"
                className="border border-white/30 text-white px-8 py-4 text-sm tracking-[0.15em] uppercase font-light hover:bg-white/10 transition-colors"
              >
                Call +91 9611560759
              </a>
              <a
                href="https://share.google/awsG9CL1kTJ5WFYSy"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/30 text-white px-6 py-4 text-sm tracking-[0.1em] uppercase font-light hover:bg-white/10 transition-colors flex items-center gap-2"
                data-testid="read-google-reviews"
              >
                <Star className="w-4 h-4 fill-[#B8860B] text-[#B8860B]" />
                Read Google Reviews
              </a>
            </div>
          </div>
        </section>

        {/* Quick Info Bar */}
        <section className="bg-[#0a0a0a] border-y border-white/10 py-6">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center md:text-left">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-3">
                <MapPin className="text-[#B8860B] flex-shrink-0" size={20} />
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Location</p>
                  <p className="text-white/80 text-sm">West of Chord Road, Basaveshwarnagar</p>
                </div>
              </div>
              <div className="flex flex-col md:flex-row items-center md:items-start gap-3">
                <Clock className="text-[#B8860B] flex-shrink-0" size={20} />
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Open Hours</p>
                  <p className="text-white/80 text-sm">10:30 AM – 10:00 PM Daily</p>
                </div>
              </div>
              <div className="flex flex-col md:flex-row items-center md:items-start gap-3">
                <Phone className="text-[#B8860B] flex-shrink-0" size={20} />
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Call Us</p>
                  <a href="tel:+919611560759" className="text-white/80 text-sm hover:text-[#B8860B]">+91 9611560759</a>
                </div>
              </div>
              <div className="flex flex-col md:flex-row items-center md:items-start gap-3">
                <Instagram className="text-[#B8860B] flex-shrink-0" size={20} />
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Instagram</p>
                  <a href="https://www.instagram.com/nailashes_basaveshwarnagar/" target="_blank" rel="noopener noreferrer" className="text-white/80 text-sm hover:text-[#B8860B]">@nailashes_basaveshwarnagar</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-16 px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-4">Welcome to Nailashes</h2>
            <h3 className="text-white text-2xl md:text-3xl font-light mb-8">
              Your Premier Nail Destination in Basaveshwarnagar
            </h3>
            
            <div className="text-white/70 space-y-6 leading-relaxed">
              <p>
                Nailashes Basaveshwarnagar brings India's largest nail chain to West Bangalore. Located on West of Chord Road, our salon has become the trusted destination for nail enthusiasts seeking premium quality services, skilled technicians, and a relaxing salon experience.
              </p>
              <p>
                Our team of certified nail technicians and lash artists brings years of experience and continuous training in the latest international techniques. From classic French manicures to intricate 3D nail art, from natural lash enhancements to dramatic volume sets – we offer the complete range of nail and beauty services under one roof.
              </p>
              <p>
                At Nailashes, we believe in using only the finest products. Our salon features premium brands including OPI, Gelish, CND Shellac, and professional-grade extension products. Combined with strict hygiene protocols – including hospital-grade sterilization and single-use tools – you can trust that your nail care is in safe hands.
              </p>
              <p>
                Whether you're preparing for a special occasion, treating yourself to some self-care, or maintaining your regular nail routine, Nailashes Basaveshwarnagar delivers exceptional results every time. Join the thousands of happy clients who have made us their go-to nail salon in Bangalore.
              </p>
            </div>
          </div>
        </section>

        {/* Services Sections */}
        <section className="py-16 px-6 md:px-12 bg-[#050505]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-4">Our Services</h2>
              <p className="text-white text-2xl md:text-3xl font-light">
                Complete Nail & Beauty Services in Basaveshwarnagar
              </p>
            </div>

            {/* Service Navigation */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {servicesSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setExpandedSection(section.id)}
                  data-testid={`service-tab-${section.id}`}
                  className={`px-5 py-2 text-sm transition-colors ${
                    expandedSection === section.id
                      ? 'bg-[#B8860B] text-black'
                      : 'bg-white/5 text-white/70 hover:bg-white/10'
                  }`}
                >
                  {section.title.split(' in ')[0]}
                </button>
              ))}
            </div>

            {/* Expanded Service Content - All sections rendered for SEO, hidden with CSS */}
            {servicesSections.map((section) => (
              <div 
                key={section.id} 
                className={`bg-[#0a0a0a] border border-white/10 p-8 md:p-12 ${
                  expandedSection === section.id ? 'block' : 'hidden'
                }`}
                data-testid={`service-section-${section.id}`}
              >
                  <h2 className="text-white text-2xl md:text-3xl font-light mb-6">
                    {section.title}
                  </h2>
                  
                  <div className="text-white/60 leading-relaxed mb-10 whitespace-pre-line">
                    {section.description}
                  </div>
                  
                  <h3 className="text-[#B8860B] text-sm tracking-wider uppercase mb-6">Pricing</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {section.services.map((service, idx) => (
                      <div key={idx} className="flex justify-between items-start p-4 bg-white/5 border border-white/5">
                        <div className="flex-1">
                          <p className="text-white font-light">{service.name}</p>
                          <p className="text-white/40 text-sm mt-1">{service.description}</p>
                          <p className="text-white/30 text-xs mt-1">{service.duration}</p>
                        </div>
                        <p className="text-[#B8860B] font-medium ml-4">{service.price}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-8 flex gap-4">
                    <button
                      onClick={() => setIsBookingModalOpen(true)}
                      className="bg-[#B8860B] text-black px-6 py-3 text-sm tracking-wider uppercase hover:bg-[#9A7B0A] transition-colors"
                    >
                      Book This Service
                    </button>
                    <Link
                      to={`/services`}
                      className="border border-white/20 text-white px-6 py-3 text-sm tracking-wider uppercase hover:bg-white/5 transition-colors"
                    >
                      View All Services
                    </Link>
                  </div>
                </div>
            ))}
          </div>
        </section>

        {/* Photo Gallery Section */}
        <section className="py-16 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-4">Our Work</h2>
              <p className="text-white text-2xl md:text-3xl font-light">
                Nailashes Basaveshwarnagar Gallery
              </p>
              <p className="text-white/50 mt-3 text-sm">Real work from our talented nail artists</p>
            </div>

            {/* Gallery Grid - Replace these with real photos */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {[
                { src: 'https://images.pexels.com/photos/3997391/pexels-photo-3997391.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'Gel nail extensions at Nailashes Basaveshwarnagar Bangalore' },
                { src: 'https://images.pexels.com/photos/3997390/pexels-photo-3997390.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'Nail art designs by Nailashes Basaveshwarnagar' },
                { src: 'https://images.pexels.com/photos/3997384/pexels-photo-3997384.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'French manicure at Nailashes salon Bangalore' },
                { src: 'https://images.pexels.com/photos/3997385/pexels-photo-3997385.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'Chrome nails done at Nailashes Basaveshwarnagar' },
                { src: 'https://images.pexels.com/photos/3997379/pexels-photo-3997379.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'Acrylic nail extensions Basaveshwarnagar' },
                { src: 'https://images.pexels.com/photos/3997386/pexels-photo-3997386.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'Spa pedicure at Nailashes Basaveshwarnagar' },
                { src: 'https://images.pexels.com/photos/3993449/pexels-photo-3993449.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'Lash extensions at Nailashes Bangalore' },
                { src: 'https://images.pexels.com/photos/3997383/pexels-photo-3997383.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'Nail technician at work Nailashes Basaveshwarnagar' },
                { src: 'https://images.pexels.com/photos/3738355/pexels-photo-3738355.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'Manicure service at Nailashes Basaveshwarnagar' },
                { src: 'https://images.pexels.com/photos/3997394/pexels-photo-3997394.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'Ombre nail design Nailashes Bangalore' },
                { src: 'https://images.pexels.com/photos/3997388/pexels-photo-3997388.jpeg?auto=compress&cs=tinysrgb&w=600', alt: '3D nail art at Nailashes Basaveshwarnagar' },
                { src: 'https://images.pexels.com/photos/3997382/pexels-photo-3997382.jpeg?auto=compress&cs=tinysrgb&w=600', alt: 'Nail salon interior Nailashes Basaveshwarnagar' },
              ].map((image, index) => (
                <div key={index} className="aspect-square overflow-hidden group">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <a
                href="https://www.instagram.com/nailashes_basaveshwarnagar/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#B8860B] hover:text-white transition-colors"
              >
                <Instagram size={20} />
                See more on Instagram @nailashes_basaveshwarnagar
              </a>
            </div>
          </div>
        </section>

        {/* Google Map Section */}
        <section className="py-16 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Map */}
              <div className="h-[400px] lg:h-[500px] bg-[#111]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.5!2d77.5369!3d12.9916!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae3d64a668919d%3A0x45d04c4384e85294!2sNailashes%20Basaveshwarnagar!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Nailashes Basaveshwarnagar Location"
                  data-testid="google-map-embed"
                ></iframe>
              </div>
              
              {/* Location Details */}
              <div className="flex flex-col justify-center">
                <h2 className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-4">Find Us</h2>
                <h3 className="text-white text-2xl md:text-3xl font-light mb-6">
                  Visit Nailashes Basaveshwarnagar
                </h3>
                
                <div className="space-y-6 text-white/70">
                  <div>
                    <p className="text-white font-medium mb-2">Full Address</p>
                    <p>LGF, No. 9, East Jayanagar,<br />West of Chord Road 1st Stage,<br />Basaveshwar Nagar, Bengaluru,<br />Karnataka 560079</p>
                  </div>
                  
                  <div>
                    <p className="text-white font-medium mb-2">Landmarks</p>
                    <p>Near West of Chord Road junction. Look for the Nailashes signboard on the lower ground floor.</p>
                  </div>
                  
                  <div>
                    <p className="text-white font-medium mb-2">Areas We Serve</p>
                    <p>Basaveshwarnagar, Rajajinagar, Vijayanagar, Mahalakshmi Layout, Kamakshipalya, Nandini Layout, and surrounding areas.</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-4 mt-8">
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=12.9916,77.5369"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#B8860B] text-black px-6 py-3 text-sm tracking-wider uppercase hover:bg-[#9A7B0A] transition-colors flex items-center gap-2"
                  >
                    <Navigation size={16} />
                    Get Directions
                  </a>
                  <a
                    href="https://share.google/awsG9CL1kTJ5WFYSy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-white/20 text-white px-6 py-3 text-sm tracking-wider uppercase hover:bg-white/5 transition-colors flex items-center gap-2"
                  >
                    <ExternalLink size={16} />
                    View on Google Maps
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-6 md:px-12 bg-[#050505]">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-4">FAQ</h2>
              <p className="text-white text-2xl md:text-3xl font-light">
                Frequently Asked Questions
              </p>
            </div>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="border border-white/10 bg-[#0a0a0a]"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    data-testid={`faq-question-${index}`}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="text-white font-light pr-4">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="text-[#B8860B] flex-shrink-0" size={20} />
                    ) : (
                      <ChevronDown className="text-white/50 flex-shrink-0" size={20} />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <p className="text-white/60 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-6 md:px-12 bg-[#B8860B]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-black text-3xl md:text-4xl font-light mb-4">
              Ready for Beautiful Nails?
            </h2>
            <p className="text-black/70 mb-8 max-w-xl mx-auto">
              Book your appointment at Nailashes Basaveshwarnagar today. Walk-ins welcome, appointments preferred.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="bg-black text-white px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-black/80 transition-colors"
              >
                Book Online
              </button>
              <a
                href="https://wa.me/919611560759?text=Hi! I'd like to book an appointment at Nailashes Basaveshwarnagar"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] text-white px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#20BD5A] transition-colors"
              >
                WhatsApp Us
              </a>
              <a
                href="tel:+919611560759"
                className="bg-white text-black px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-white/90 transition-colors"
              >
                Call Now
              </a>
            </div>
          </div>
        </section>

        {/* Nailashes-Specific Footer Note */}
        <section className="py-8 px-6 md:px-12 bg-[#0a0a0a] border-t border-white/10">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-white/50 text-sm" data-testid="brand-clarification-note">
              Nailashes Basaveshwarnagar is part of the{' '}
              <Link to="/" className="text-[#B8860B] hover:underline">BLR Nail Club</Link>{' '}
              family of premium nail salons in Bangalore.{' '}
              <Link to="/about" className="text-white/70 hover:text-[#B8860B]">Learn more about us →</Link>
            </p>
          </div>
        </section>
      </main>

      <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
    </>
  );
};

export default NailashesBasaveshwarnagar;
